import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, Clock, Route, Send, Loader2, Wrench, Mail } from "lucide-react";
import api, { apiError } from "../lib/api";
import { PageHeader } from "../components/Layout";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { hasPro } from "../lib/pro";
import { ChecklistFill, checklistPayload, missingRequired } from "../components/ProChecklist";

export const CHECKLIST_422 = "Tick every required checklist item (*) before sending.";

const toMin = (s) => { const m = /^(\d{1,2}):(\d{2})$/.exec(s || ""); return m && +m[1] < 24 && +m[2] < 60 ? +m[1] * 60 + +m[2] : null; };
// Planned start/end become the first hours line, only when both are valid and end > start.
export function jobHoursLine(job) {
  const a = toMin(job.start_time), b = toMin(job.end_time);
  if (a == null || b == null || b <= a) return null;
  return { date: job.date, work_hours: Math.round(((b - a) / 60) * 100) / 100, travel_hours: "", description: `${job.start_time}–${job.end_time}` };
}

// Checklist templates (Pro) keyed by machine type id; super admins have no company scope here.
export function useProTypeMap(user) {
  const [map, setMap] = useState({});
  useEffect(() => {
    if (!hasPro(user) || user?.role === "super_admin") return;
    api.get("/pro/machine-types").then(({ data }) => setMap(Object.fromEntries(data.map((x) => [x.id, x])))).catch(() => {});
  }, [user]);
  return map;
}

export const withChecklists = (machines, typeMap) => machines.map((m) => {
  const { checklist, ...rest } = m; // eslint-disable-line no-unused-vars
  const c = checklistPayload(m, typeMap);
  if (c) return { ...rest, checklist: c };
  // No template loaded (or Pro off): keep stored entries so an edit never wipes them.
  return checklist?.length ? { ...rest, checklist: checklist.map(({ item_id, done, note, photo_id }) => ({ item_id, done: !!done, note: note || "", photo_id: photo_id || null })) } : rest;
});

export function Eyebrow({ children }) { return <span className="text-[11px] font-bold tracking-wider text-[#F26722] uppercase mb-2 block mt-7">{children}</span>; }
export const inputCls = "w-full bg-[#1c1c1f] border border-zinc-800 text-white rounded-xl px-4 py-3 placeholder-zinc-600 focus:border-[#F26722] focus:outline-none";

export function SignaturePad({ onChange, initial }) {
  const ref = useRef(null); const drawing = useRef(false); const [has, setHas] = useState(!!initial);
  useEffect(() => { const ctx = ref.current.getContext("2d"); ctx.strokeStyle = "#111"; ctx.lineWidth = 2.5; ctx.lineCap = "round"; if (initial) { const img = new Image(); img.onload = () => ctx.drawImage(img, 0, 0, ref.current.width, ref.current.height); img.src = initial; } }, [initial]);
  const pos = (e) => { const c = ref.current, rect = c.getBoundingClientRect(), p = e.touches ? e.touches[0] : e; return { x: (p.clientX - rect.left) * (c.width / rect.width), y: (p.clientY - rect.top) * (c.height / rect.height) }; };
  const start = (e) => { drawing.current = true; const { x, y } = pos(e); const ctx = ref.current.getContext("2d"); ctx.beginPath(); ctx.moveTo(x, y); };
  const move = (e) => { if (!drawing.current) return; e.preventDefault(); const { x, y } = pos(e); const ctx = ref.current.getContext("2d"); ctx.lineTo(x, y); ctx.stroke(); setHas(true); };
  const end = () => { if (drawing.current && has) onChange(ref.current.toDataURL("image/png")); drawing.current = false; };
  const clear = () => { ref.current.getContext("2d").clearRect(0, 0, ref.current.width, ref.current.height); setHas(false); onChange(""); };
  return (<div><div className="bg-white rounded-xl overflow-hidden"><canvas ref={ref} width={600} height={180} className="w-full touch-none" data-testid="signature-canvas" onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end} onTouchStart={start} onTouchMove={move} onTouchEnd={end} /></div><button type="button" onClick={clear} className="text-xs text-zinc-400 mt-2">{sigClearLabel()}</button></div>);
}
export function SenderNote({ className = "" }) {
  const { t } = useLang();
  return <p data-testid="report-sender-note" className={`text-xs text-zinc-500 flex items-start gap-1.5 ${className}`}><Mail size={13} className="mt-0.5 shrink-0 text-[#F26722]" /> {t("Reports are sent from RV Service Tool on your behalf, shown with your name, and replies come back to your email address.")}</p>;
}
export const sentMsg = (t, office, email) => t("Sent to {office} — replies will reach {email}.").replace("{office}", office || t("office")).replace("{email}", email || "");

function sigClearLabel() {
  const nlOn = (typeof localStorage !== "undefined" && localStorage.getItem("app_lang") === "nl");
  return nlOn ? "Handtekening wissen" : "Clear signature";
}

export default function NewReport() {
  const nav = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { t, lang } = useLang();
  const typeMap = useProTypeMap(user);
  const [jobId, setJobId] = useState(null);
  const [plannerNotes, setPlannerNotes] = useState("");
  const [sp] = useSearchParams();
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const [f, setF] = useState({ customer_name: "", address: "", contact_person: "", call_id: "", report_date: today, work_report: "", recipient_id: "" });
  const [reportLang, setReportLang] = useState(lang);
  const [sig, setSig] = useState("");
  const [sigName, setSigName] = useState("");
  const [machines, setMachines] = useState([{ serial_number: "", parts: [] }]);
  const [hours, setHours] = useState([]);
  const [trips, setTrips] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [machineTypes, setMachineTypes] = useState([]);

  useEffect(() => { api.get("/machine-types").then(({ data }) => setMachineTypes(data)).catch(() => {}); }, []);

  useEffect(() => {
    api.get("/recipients").then(({ data }) => { setRecipients(data); setF((s) => ({ ...s, recipient_id: user?.default_recipient_id || data[0]?.id || "" })); });
  }, [user]);

  useEffect(() => { api.get("/parts").then(({ data }) => setCatalog(data)).catch(() => {}); }, []);

  // "Start report" from a planned job: load it and pre-fill the form; the report is linked via job_id.
  useEffect(() => {
    const id = sp.get("job_id") || location.state?.job?.id;
    if (!id) return;
    const cid = sp.get("company_id");
    api.get(`/pro/jobs/${id}`, { params: cid ? { company_id: cid } : {} })
      .then(({ data: job }) => {
        setJobId(job.id);
        setPlannerNotes(job.notes || "");
        setF((s) => ({ ...s, customer_name: job.customer_name || "", address: job.address || "", contact_person: job.contact_person || "", call_id: job.call_id || "", report_date: job.date || s.report_date }));
        if ((job.machines || []).length) setMachines(job.machines.map((m) => ({ serial_number: m.serial_number || "", machine_type_id: m.machine_type_id || null, parts: [] })));
        const line = jobHoursLine(job);
        if (line) setHours([line]);
      })
      .catch((e) => toast.error(apiError(e)));
  }, [sp, location.state]); // eslint-disable-line react-hooks/exhaustive-deps

  // Parts sent over from the Parts Book "Add to report" land on the first machine.
  useEffect(() => {
    const pending = JSON.parse(localStorage.getItem("arv_pending_parts") || "[]");
    if (pending.length) {
      setMachines((ms) => {
        const first = ms[0] || { serial_number: "", parts: [] };
        const have = new Set((first.parts || []).map((p) => p.part_number));
        const merged = [...(first.parts || []), ...pending.filter((p) => !have.has(p.part_number)).map((p) => ({ ...p, quantity: 1 }))];
        return [{ ...first, parts: merged }, ...ms.slice(1)];
      });
      localStorage.removeItem("arv_pending_parts");
      toast.success(`${pending.length} part(s) added from Parts Book`);
    }
  }, []);

  if (user?.role === "office") {
    return (<><PageHeader title={t("New Report")} /><p className="text-zinc-400 px-4 pt-6">{t("Office accounts can view and re-send reports but cannot create them.")}</p></>);
  }

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const setMachine = (i, k, v) => setMachines(machines.map((m, j) => j === i ? { ...m, [k]: v } : m));
  const addPart = (i, part) => setMachines(machines.map((m, j) => j === i ? { ...m, parts: [...m.parts, part] } : m));
  const removePart = (i, pi) => setMachines(machines.map((m, j) => j === i ? { ...m, parts: m.parts.filter((_, k) => k !== pi) } : m));

  const submit = async (isDraft = false) => {
    if (!f.customer_name.trim()) return toast.error(t("Customer name is required"));
    if (!isDraft) {
      if (!machines.some((m) => (m.parts || []).length > 0)) return toast.error(t("Add at least one part to a machine"));
      if (!f.recipient_id) return toast.error(t("Select an office recipient"));
      const miss = missingRequired(machines, typeMap);
      if (miss.length) return toast.error(`${t(CHECKLIST_422)} ${t("Machine")} ${miss.join(", ")}`);
    }
    setLoading(true);
    const body = { ...f, machines: withChecklists(machines, typeMap), hours, trips, language: reportLang, is_draft: isDraft };
    if (jobId) body.job_id = jobId;
    const custName = sigName.trim() || f.contact_person || f.customer_name;
    if (sig || custName) body.signature = { image: sig, name: custName, date: f.report_date };
    try {
      await api.post("/reports", body);
      const rec = recipients.find((o) => o.id === f.recipient_id);
      toast.success(isDraft ? t("Draft saved") : sentMsg(t, rec?.name || rec?.email, user?.email));
      nav("/reports");
    } catch (err) {
      const st = err.response?.status;
      if (!isDraft && (!st || st >= 500)) { toast.success(t("Report saved. Office email may take a moment.")); nav("/reports"); }
      else toast.error(st === 422 ? t(CHECKLIST_422) : apiError(err));
    } finally { setLoading(false); }
  };

  return (
    <>
      <PageHeader title={t("New Report")} subtitle={t("Log a service call & its machines")} />
      <div className="px-4 pb-4">
        <div className="flex items-center justify-between mt-4 bg-[#18181B] border border-zinc-800 rounded-xl px-4 py-3" data-testid="report-lang-row">
          <span className="text-sm text-zinc-300">{t("Report language")}</span>
          <div className="inline-flex rounded-lg border border-zinc-700 overflow-hidden">
            {["en", "nl"].map((l) => (
              <button key={l} type="button" onClick={() => setReportLang(l)} data-testid={`report-lang-${l}`}
                className={`px-3 py-1.5 text-xs font-bold transition-colors ${reportLang === l ? "bg-[#F26722] text-white" : "text-zinc-400 hover:text-white"}`}>
                {l.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
        {jobId && <p className="text-xs font-semibold mt-3" style={{ color: "#B45309" }} data-testid="report-from-job">{t("Filled in from a planned job. Sending this report marks the job as done.")}</p>}
        {plannerNotes && (
          <div className="mt-3 bg-[#3D6080]/10 border border-[#3D6080]/40 rounded-xl p-3" data-testid="planner-notes">
            <p className="text-[11px] font-bold uppercase tracking-wider text-[#3D6080]">{t("Planner notes")}</p>
            <p className="text-sm text-zinc-300 whitespace-pre-wrap mt-1">{plannerNotes}</p>
            <p className="text-[11px] text-zinc-500 mt-1.5">{t("Only for you — not part of the report, PDF or email.")}</p>
          </div>
        )}
        <Eyebrow>{t("Customer & Call")}</Eyebrow>
        <div className="space-y-3">
          <div><label className="text-xs text-zinc-400">{t("Customer name")}</label><input data-testid="customer-name-input" className={inputCls} value={f.customer_name} onChange={set("customer_name")} placeholder="Acme Bakery" /></div>
          <div><label className="text-xs text-zinc-400">{t("Address")}</label><input data-testid="address-input" className={inputCls} value={f.address} onChange={set("address")} placeholder="12 Mill Street" /></div>
          <div><label className="text-xs text-zinc-400">{t("Contact person")}</label><input data-testid="contact-input" className={inputCls} value={f.contact_person} onChange={set("contact_person")} placeholder="Jane Roe" /></div>
          <div><label className="text-xs text-zinc-400">{t("Call ID")}</label><input data-testid="callid-input" className={inputCls} value={f.call_id} onChange={set("call_id")} placeholder="CALL-7781" /></div>
          <div><label className="text-xs text-zinc-400">{t("Date")}</label><input data-testid="date-input" type="date" className={inputCls} value={f.report_date} onChange={set("report_date")} /></div>
        </div>
        <p className="text-xs text-zinc-500 mt-2 flex items-center gap-1"><Wrench size={12} /> {t("Mechanic")}: {user?.name}</p>

        <Eyebrow>{t("Machines")} ({machines.length})</Eyebrow>
        {machines.map((m, i) => (<MachineCard key={i} m={m} idx={i} catalog={catalog} machineTypes={machineTypes} checklistTpl={typeMap[m.machine_type_id]?.checklist} onChecklist={(v) => setMachine(i, "checklist", v)} onType={(v) => setMachine(i, "machine_type_id", v)} onSerial={(v) => setMachine(i, "serial_number", v)} onAdd={(p) => addPart(i, p)} onRemove={(pi) => removePart(i, pi)} />))}
        <button data-testid="add-machine-btn" onClick={() => setMachines([...machines, { serial_number: "", parts: [] }])} className="w-full border border-dashed border-zinc-700 text-[#F26722] rounded-xl py-3 mt-2 font-semibold flex items-center justify-center gap-2"><Plus size={18} /> {t("Add another machine")}</button>

        <Eyebrow>{t("Hours")}</Eyebrow>
        {hours.map((h, i) => (
          <div key={i} className="grid grid-cols-4 gap-2 mb-2">
            <input className={inputCls} type="date" value={h.date} onChange={(e) => setHours(hours.map((x, j) => j === i ? { ...x, date: e.target.value } : x))} />
            <input className={inputCls} type="number" placeholder="work h" value={h.work_hours} onChange={(e) => setHours(hours.map((x, j) => j === i ? { ...x, work_hours: e.target.value } : x))} />
            <input className={inputCls} type="number" placeholder="travel h" value={h.travel_hours} onChange={(e) => setHours(hours.map((x, j) => j === i ? { ...x, travel_hours: e.target.value } : x))} />
            <div className="flex gap-1"><input className={inputCls} placeholder="desc" value={h.description} onChange={(e) => setHours(hours.map((x, j) => j === i ? { ...x, description: e.target.value } : x))} /><button onClick={() => setHours(hours.filter((_, j) => j !== i))} className="text-zinc-500"><Trash2 size={16} /></button></div>
          </div>
        ))}
        <button data-testid="add-hours-btn" onClick={() => setHours([...hours, { date: today, work_hours: "", travel_hours: "", description: "" }])} className="w-full border border-dashed border-zinc-700 text-[#F26722] rounded-xl py-3 font-semibold flex items-center justify-center gap-2"><Clock size={18} /> {t("Add hours entry")}</button>

        <Eyebrow>{t("Trips")}</Eyebrow>
        {trips.map((t, i) => (
          <div key={i} className="grid grid-cols-4 gap-2 mb-2">
            <input className={inputCls} placeholder="From" value={t.from_location} onChange={(e) => setTrips(trips.map((x, j) => j === i ? { ...x, from_location: e.target.value } : x))} />
            <input className={inputCls} placeholder="To" value={t.to_location} onChange={(e) => setTrips(trips.map((x, j) => j === i ? { ...x, to_location: e.target.value } : x))} />
            <input className={inputCls} type="number" placeholder="km" value={t.distance_km} onChange={(e) => setTrips(trips.map((x, j) => j === i ? { ...x, distance_km: e.target.value } : x))} />
            <div className="flex gap-1"><input className={inputCls} placeholder="time" value={t.travel_time} onChange={(e) => setTrips(trips.map((x, j) => j === i ? { ...x, travel_time: e.target.value } : x))} /><button onClick={() => setTrips(trips.filter((_, j) => j !== i))} className="text-zinc-500"><Trash2 size={16} /></button></div>
          </div>
        ))}
        <button data-testid="add-trip-btn" onClick={() => setTrips([...trips, { date: today, from_location: "", to_location: "", distance_km: "", travel_time: "" }])} className="w-full border border-dashed border-zinc-700 text-[#F26722] rounded-xl py-3 font-semibold flex items-center justify-center gap-2"><Route size={18} /> {t("Add trip")}</button>

        <Eyebrow>{t("Work Performed")}</Eyebrow>
        <textarea data-testid="work-performed-input" className={inputCls + " min-h-32"} value={f.work_report} onChange={set("work_report")} placeholder={t("Describe what was done during the job...")} />

        <Eyebrow>{t("Customer Signature")}</Eyebrow>
        <div className="mb-3"><label className="text-xs text-zinc-400">{t("Customer name")}</label><input data-testid="signature-name-input" className={inputCls} value={sigName} onChange={(e) => setSigName(e.target.value)} placeholder={t("Name of person signing")} /></div>
        <SignaturePad onChange={setSig} />

        <Eyebrow>{t("Send to Office")}</Eyebrow>
        {recipients.length === 0 && <p className="text-xs text-zinc-500">{t("No recipients yet. Add one in Profile → Recipients.")}</p>}
        <div className="space-y-2">
          {recipients.map((o) => (
            <button key={o.id} data-testid={`recipient-${o.id}`} onClick={() => setF({ ...f, recipient_id: o.id })}
              className={`w-full flex items-center justify-between p-4 rounded-xl border transition-all ${f.recipient_id === o.id ? "border-[#F26722] bg-[#3B1D0E]/40" : "border-zinc-800 bg-[#18181B]"}`}>
              <div className="text-left"><p className="font-bold text-white">{o.name}</p><p className="text-xs text-zinc-500">{o.email}</p></div>
              <div className={`w-5 h-5 rounded-full border-2 ${f.recipient_id === o.id ? "border-[#F26722] bg-[#F26722]" : "border-zinc-600"}`} />
            </button>
          ))}
        </div>
        <SenderNote className="mt-3" />

        <button data-testid="send-report-btn" onClick={() => submit(false)} disabled={loading} className="w-full bg-[#F26722] hover:bg-[#D95513] disabled:opacity-60 text-white font-bold py-4 rounded-xl mt-6 flex items-center justify-center gap-2">
          {loading ? <Loader2 className="animate-spin" size={20} /> : <><Send size={18} /> {t("Send Report to Office")}</>}
        </button>
        <button data-testid="save-draft-btn" onClick={() => submit(true)} disabled={loading} className="w-full bg-[#18181B] border border-zinc-800 hover:border-zinc-700 disabled:opacity-60 text-zinc-200 font-bold py-3.5 rounded-xl mt-3 flex items-center justify-center gap-2">
          {t("Save as Draft")}
        </button>
      </div>
    </>
  );
}

export function MachineCard({ m, idx, onSerial, onAdd, onRemove, onType, catalog = [], machineTypes = [], checklistTpl, onChecklist }) {
  const { t } = useLang();
  const [pn, setPn] = useState(""); const [pname, setPname] = useState(""); const [qty, setQty] = useState(1);
  const [focused, setFocused] = useState(false);
  const [typeParts, setTypeParts] = useState(null);
  const add = () => { if (!pn.trim()) return; onAdd({ part_number: pn.trim(), part_name: pname.trim() || pn.trim(), quantity: Number(qty) || 1 }); setPn(""); setPname(""); setQty(1); };

  useEffect(() => {
    if (!m.machine_type_id) { setTypeParts(null); return; }
    let active = true;
    api.get(`/machine-types/${m.machine_type_id}/parts`).then(({ data }) => { if (active) setTypeParts(data); }).catch(() => { if (active) setTypeParts([]); });
    return () => { active = false; };
  }, [m.machine_type_id]);

  const q = pn.trim().toLowerCase();
  const onMachine = new Set((m.parts || []).map((p) => (p.part_number || "").toLowerCase()));
  const source = m.machine_type_id ? (typeParts || []) : catalog;
  let sugg = [];
  if (q.length >= 1) {
    const pool = source.filter((c) => !onMachine.has((c.part_number || "").toLowerCase()));
    const num = (c) => (c.part_number || "").toLowerCase();
    const desc = (c) => (c.part_name || "").toLowerCase();
    const wordStarts = (c) => desc(c).split(/\s+/).some((w) => w.startsWith(q));
    const t1 = pool.filter((c) => num(c).startsWith(q));
    const t2 = pool.filter((c) => !num(c).startsWith(q) && wordStarts(c));
    const t3 = pool.filter((c) => !num(c).startsWith(q) && !wordStarts(c) && num(c).includes(q));
    const t4 = pool.filter((c) => !num(c).startsWith(q) && !wordStarts(c) && !num(c).includes(q) && desc(c).includes(q));
    const seen = new Set();
    sugg = [...t1, ...t2, ...t3, ...t4].filter((c) => { const k = c.id || c.part_number; if (seen.has(k)) return false; seen.add(k); return true; }).slice(0, 6);
    if (sugg.length === 1 && num(sugg[0]) === q) sugg = [];
  }
  const pick = (c) => { onAdd({ part_number: c.part_number, part_name: c.part_name || c.part_number, quantity: 1 }); setPn(""); setPname(""); setQty(1); setFocused(false); };

  return (
    <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-4 mb-3">
      <p className="font-bold text-white text-base">{t("Machine")} {idx + 1}</p>
      <input className={inputCls + " mt-3"} placeholder={t("Serial number")} value={m.serial_number} onChange={(e) => onSerial(e.target.value)} data-testid={`machine-serial-${idx}`} />
      {machineTypes.length > 0 && onType && (
        <select className={inputCls + " mt-2"} value={m.machine_type_id || ""} onChange={(e) => onType(e.target.value || null)} data-testid={`machine-type-${idx}`}>
          <option value="">{t("Machine type")}: {t("None")}</option>
          {machineTypes.map((mt) => <option key={mt.id} value={mt.id}>{mt.name}</option>)}
        </select>
      )}
      <div className="mt-3 space-y-2">
        <div className="relative">
          <input className={inputCls} placeholder={t("Part number")} value={pn}
            onChange={(e) => setPn(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setTimeout(() => setFocused(false), 150)}
            data-testid={`part-number-${idx}`} />
          {focused && sugg.length > 0 && (
            <div className="absolute z-30 left-0 right-0 mt-1 bg-[#1c1c1f] border border-zinc-700 rounded-xl overflow-hidden shadow-xl" data-testid={`part-suggestions-${idx}`}>
              {sugg.map((c) => (
                <button key={c.id || c.part_number} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => pick(c)}
                  data-testid={`part-suggestion-${c.part_number}`}
                  className="w-full text-left px-3 py-2 hover:bg-[#262629] flex flex-col border-b border-zinc-800 last:border-0">
                  <span className="text-sm text-white font-mono">{c.part_number}</span>
                  <span className="text-xs text-zinc-500">{c.part_name}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <div className="flex-1 min-w-0"><input className={inputCls} placeholder={t("Name (optional)")} value={pname} onChange={(e) => setPname(e.target.value)} /></div>
          <input className="w-20 shrink-0 bg-[#1c1c1f] border border-zinc-800 text-white rounded-xl px-3 py-3 text-center focus:border-[#F26722] focus:outline-none" type="number" min={1} value={qty} onChange={(e) => setQty(e.target.value)} data-testid={`part-qty-${idx}`} />
          <button onClick={add} className="shrink-0 bg-[#F26722] text-white rounded-xl px-4" data-testid={`add-part-${idx}`}><Plus size={18} /></button>
        </div>
      </div>
      <div className="mt-2 space-y-1">
        {m.parts.length === 0 && <p className="text-xs text-zinc-500 italic">{t("No parts added.")}</p>}
        {m.parts.map((p, j) => (
          <div key={j} className="flex justify-between items-center bg-[#141416] rounded-lg px-3 py-2 text-sm">
            <span className="text-zinc-200">{p.part_name} <span className="text-[#F97316] font-mono">{p.part_number} ×{p.quantity}</span></span>
            <button onClick={() => onRemove(j)} className="text-zinc-500"><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
      {onChecklist && <ChecklistFill template={checklistTpl} value={m.checklist} onChange={onChecklist} idx={idx} />}
    </div>
  );
}
